/**
 * EmbedChart — the root of a single-chart iframe embed.
 *
 * A lean sibling of BudgetWidget rather than a mode flag threaded through its
 * 1450 lines. It reuses the same data hooks verbatim (so an embed and the
 * dashboard can never disagree about what a filter means) but renders exactly
 * one chart, with every navigation callback a no-op.
 *
 * The view is STATIC: nothing inside the frame changes it, so there is no
 * reducer here — the decoded config IS the state. That is what makes an embed a
 * stable citation: the chart a reader sees is the chart its author chose.
 *
 * Deliberately absent: Spotlights, SearchAutocomplete, ActiveFilterChips,
 * FilterPanel, BudgetTable, and the type/unit controls (those live in
 * BudgetWidget's TopControlsRow, so simply not rendering them locks the view).
 * Also absent: apiClient and onToggleTitleBreakout on the area chart, which is
 * what suppresses the Sonstiges picker and the title-point popover.
 *
 * Analytics are NOT fired from here. The embed page's own HTML request carries
 * the parent page in its Referer, so apps/api logs the load server-side —
 * keeping the "derived server-side, never trusted from the client" property of
 * analytics.widget_loads. Calling trackPageview() here would double-count and
 * record our own host.
 */

import { useMemo } from 'react';
import styled from '@emotion/styled';
import { BudgetApiClient } from './api/client';
import { ExpenditureAreaChart } from './components/ExpenditureAreaChart';
import { ExpenditureBarChart } from './components/ExpenditureBarChart';
import { useBudgetData } from './hooks/useBudgetData';
import { useAsync } from './hooks/useAsync';
import { useFilterOptions } from './hooks/useFilterOptions';
import { useTitleDetails } from './hooks/useTitleDetails';
import { theme } from './styles/theme';
import { Spinner } from './styles/components';
import type { EmbedConfig } from './utils/embedConfig';
import type { SelectedTitle } from './types';
import { useT } from './i18n';
// Same wordmark file chartExport.ts stamps onto PNG/SVG exports, inlined at
// build time so it travels inside the bundle. A runtime fetch would resolve
// against the frame's own root, and the wordmark would silently drop out.
import brandLogoSvg from './assets/corporate-brand-dz.svg?raw';

export interface EmbedChartProps {
  /** Same-origin in the frame, so '/api' — no CORS involved. */
  apiBaseUrl: string;
  config: EmbedConfig;
  /** Where "Zum vollständigen Haushaltstracker" points. */
  trackerUrl: string;
}

const Frame = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  box-sizing: border-box;
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  font-family: ${theme.fontFamily};
`;

const ChartArea = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// Source + wordmark + link out. Mirrors the footer chartExport.ts draws onto a
// PNG, so a live embed and an exported image credit the data identically.
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  margin-top: ${theme.spacing.sm};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${theme.colors.border};
`;

const SourceText = styled.span`
  font-family: var(--sans-stack);
  font-size: 11px;
  color: #000000;
`;

// 96px wide (vs 150px on an exported card): the embed footer is a single line
// next to the source text, not the bottom of a print-scale chart.
const BrandLogo = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;

  svg {
    width: 96px;
    height: auto;
    display: block;
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const TrackerLink = styled.a`
  font-family: var(--sans-stack);
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  min-height: 240px;
  text-align: center;
`;

const NoticeTitle = styled.p`
  margin: 0;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.md};
  color: ${theme.colors.text.primary};
`;

const NoticeBody = styled.p`
  margin: 0;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
  max-width: 34em;
`;

export function EmbedChart({ apiBaseUrl, config, trackerUrl }: EmbedChartProps) {
  const m = useT();
  const apiClient = useMemo(() => new BudgetApiClient(apiBaseUrl), [apiBaseUrl]);
  const { filters, drillDownType, drillDownSubType, displayUnit } = config;
  const budgetType = filters.type;

  // A URL can only carry the title id, so the label comes from the API. That
  // also means a renamed Zweckbestimmung follows automatically instead of the
  // embed freezing whatever text was current when the snippet was copied.
  const { data: titleDetails } = useTitleDetails(apiClient, config.titleId, budgetType);
  const selectedTitle: SelectedTitle | null = useMemo(() => {
    if (!config.titleId) return null;
    if (!titleDetails) return { id: config.titleId, zweckbestimmung: '' };
    return {
      id: config.titleId,
      zweckbestimmung: titleDetails.zweckbestimmung,
      titelFunktion: titleDetails.titelFunktion,
      prefix: titleDetails.prefix ?? undefined,
    };
  }, [config.titleId, titleDetails]);

  const {
    data,
    subData,
    titleData,
    subColorMap,
    titleColorMap,
    isLoading,
    isLoadingSubData,
    isLoadingTitleData,
    error,
  } = useBudgetData(
    apiClient,
    filters,
    drillDownType,
    drillDownSubType,
    selectedTitle,
    config.brokenOutTitleIds,
  );

  const { data: gdpData, error: gdpError } = useAsync(
    () => apiClient.getGdpData(),
    [apiClient],
  );
  const { data: budgetTotals, error: budgetTotalsError } = useAsync(
    () => apiClient.getBudgetTotals(budgetType),
    [apiClient, budgetType],
  );
  const { options: filterOptions } = useFilterOptions(apiClient, filters.einzelplan, budgetType);

  // Same derivation as BudgetWidget: total_ist is 0 (never null) for plan years,
  // so "has actuals" is > 0.
  const yearsWithIst = useMemo(() => {
    if (!budgetTotals) return new Set<number>();
    return new Set(
      budgetTotals.filter(t => t.total_ist != null && t.total_ist > 0).map(t => t.year),
    );
  }, [budgetTotals]);

  const latestDataYear = useMemo(() => {
    const years = budgetTotals?.length
      ? budgetTotals.map(t => t.year)
      : (data?.expenditureByYearAndType ?? []).map(d => d.year);
    return years.length ? Math.max(...years) : new Date().getFullYear();
  }, [budgetTotals, data]);
  const effectiveYear = config.year ?? latestDataYear;

  // Unit data gates the first paint exactly as it does in the dashboard:
  // without budgetTotals every year renders as Soll and the areas visibly
  // shrink when the totals land. Each term clears on error so a failed fetch
  // degrades rather than spinning forever.
  const isUnitDataLoading =
    (budgetTotals === null && !budgetTotalsError) ||
    (displayUnit === 'gdp_percent' && gdpData === null && !gdpError);

  const activeFilters = useMemo(
    () => ({
      einzelplan: filters.einzelplan,
      kapitel: filters.kapitel,
      funktion: filters.funktion,
      oberfunktion: filters.oberfunktion,
      hauptfunktion: filters.hauptfunktion,
      gruppierung: filters.gruppierung,
      obergruppe: filters.obergruppe,
      hauptgruppe: filters.hauptgruppe,
      prefix: filters.prefix,
      kapitelScope: filters.kapitelScope,
      // Never the free-text search: the API strips `q`, so captioning an embed
      // with it would claim a filter that was never applied.
      query: null,
    }),
    [filters],
  );

  const filterOptionsLookup = useMemo(
    () =>
      filterOptions
        ? {
            einzelplan: filterOptions.einzelplan,
            kapitel: filterOptions.kapitel,
            funktion: filterOptions.funktion,
            oberfunktion: filterOptions.oberfunktion,
            hauptfunktion: filterOptions.hauptfunktion,
            gruppierung: filterOptions.gruppierung,
            obergruppe: filterOptions.obergruppe,
            hauptgruppe: filterOptions.hauptgruppe,
          }
        : null,
    [filterOptions],
  );

  const footer = (
    <Footer>
      {/* Same credit line chartExport.ts stamps onto exported images. */}
      <SourceText>{m.common.source}</SourceText>
      <FooterRight>
        <TrackerLink href={trackerUrl} target="_blank" rel="noopener noreferrer">
          {m.embed.trackerLink}
        </TrackerLink>
        <BrandLogo
          aria-label="Dezernat Zukunft"
          role="img"
          dangerouslySetInnerHTML={{ __html: brandLogoSvg }}
        />
      </FooterRight>
    </Footer>
  );

  // A breakdown for a drilled view arrives after the root series, so the
  // "unavailable" screen must wait for the level the chart will actually draw —
  // otherwise a drilled embed flashes "nicht mehr verfügbar" on every load.
  const isLoadingLevel =
    isLoading ||
    (drillDownType !== null && isLoadingSubData) ||
    (drillDownSubType !== null && isLoadingTitleData);

  if (isLoadingLevel || isUnitDataLoading) {
    return (
      <Frame>
        <Centered>
          <Spinner />
        </Centered>
        {footer}
      </Frame>
    );
  }

  // An embed URL outlives the data it points at: budget.entries is reloaded
  // externally, so a Kapitel or Titel named in a published snippet can vanish.
  // Say so plainly rather than silently falling back to the unfiltered budget —
  // that would show completely different numbers under the author's caption.
  //
  // Count rows at the level the chart actually draws, mirroring its own
  // selection: title level draws titleData, sub level subData, root the
  // per-type series. Checking only the root series would call a perfectly good
  // drilled embed "unavailable" whenever its root query happens to be empty.
  const levelRows =
    drillDownSubType !== null
      ? titleData?.length ?? 0
      : drillDownType !== null
        ? subData?.length ?? 0
        : data?.expenditureByYearAndType.length ?? 0;

  // `data` is also the charts' required root-series prop, so a null one is
  // unrenderable regardless of level.
  if (error || !data || levelRows === 0) {
    return (
      <Frame>
        <Centered>
          <NoticeTitle>{m.embed.unavailableTitle}</NoticeTitle>
          <NoticeBody>{m.embed.unavailableBody}</NoticeBody>
          <TrackerLink href={trackerUrl} target="_blank" rel="noopener noreferrer">
            {m.embed.trackerLink}
          </TrackerLink>
        </Centered>
        {footer}
      </Frame>
    );
  }

  const shared = {
    data: data.expenditureByYearAndType,
    subData,
    titleData,
    subColorMap,
    titleColorMap,
    isLoading,
    isLoadingSubData,
    isLoadingTitleData,
    drillDownType,
    drillDownSubType,
    selectedTitle,
    budgetType,
    displayUnit,
    gdpData,
    budgetTotals,
    yearsWithIst,
    activeFilters,
    filterOptions: filterOptionsLookup,
    // Locked view: every navigation affordance resolves to nothing.
    onDrillDown: noop,
    onDrillDownSub: noop,
    onDrillUp: noop,
    onFilterBySubType: noop,
    onFilterByTitle: noop,
    onNavigateToRoot: noop,
    onNavigateToLevel2: noop,
    onDeselectTitle: noop,
    onClearFilter: undefined,
    // Locked, and with no export menu the frame needs no icon font.
    interactive: false,
    showExport: false,
  };

  return (
    <Frame>
      <ChartArea>
        {config.chart === 'area' ? (
          <ExpenditureAreaChart {...shared} activeFilter={filters.expenditureType} />
        ) : (
          <ExpenditureBarChart
            {...shared}
            activeType={filters.expenditureType}
            selectedYear={effectiveYear}
            onYearChange={noop}
            startExpanded
            hideYearSelect
          />
        )}
      </ChartArea>
      {footer}
    </Frame>
  );
}

function noop() {}
